import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Layout from "../../components/Layout/Layout";
import MuiAlert from "../../components/Alert/Alert";
import Search from "../../components/Search/Search";
import Tabs from "../../components/Tabs/Tabs";
import Show from "../../components/Show/Show";
import NewButton from "../../components/NewButton/NewButton";
import RecievedReportsUI from "./ReceivedReportsUI";
import { IFile, IReport, ITextInput } from "../../helpers/Interfaces";
import ApiService from "../../services/ApiService";
import DraftReportsUI from "./DraftReportsUI";
import PendingReportsUI from "./PendingReportsUI";
import AddReport from "../../components/AddReport/AddReport";
import EditReport from "../../components/EditReport/EditReport";
import CompletedReportsUI from "./CompletedReportsUI";
import { useNavigate, useParams } from "react-router-dom";
import ViewDocumentModal from "../../components/ViewDocumentModal/ViewDocumentModal";
import { ReportFilterTypes } from "../../helpers/Types";
import { useSearchParams } from 'react-router-dom';
import { AppointmentReport } from "../../store/Store";
import DeletePrompt from "../../components/DeletePrompt/DeletePrompt";
import InputDropDown from "../../components/InputDropDown/InputDropDown";
import DateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import DocumentButton from "../../components/DocumentButton/DocumentButton";
import moment from "moment";
import ConfirmationPrompt from "../../components/ConfirmationPrompt/ConfirmationPrompt";
import { fetchMediaArray, getMediaBlob } from "../../helpers/FirebaseFunctions";
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { getFileNameForZip } from "../../helpers/HelperFunctions";
import GenerateLinkModal from "../../components/GenerateLinkModal/GenerateLinkModal";
import EditReport2 from "../../components/EditReport2/EditReport2";

import "./Reports.scss";

export default function Reports() {

  const apiService = new ApiService();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [reportModals, setReportModals] = useState({
    add: false, edit: false, view: false, bulkDelete: false, bulkDownload: false, generateLink: false, edit2: false
  })
  const [reports, setReports] = useState([] as IReport[]);
  const [selectedReport, setSelectedReport] = useState({} as IReport);
  const [showAlert, setShowAlert] = useState({ isError: false, message: "", open: false });
  const [filteredReports, setFilteredReports] = useState([] as IReport[]);
  const [showCancelFilter, setShowCancelFilter] = useState(false);
  const [tickedReports, setTickedReports] = useState([] as string[]);
  const [filterForm, setFilerForm] = useState
    <{ selectedDateFilter: string, startDate: moment.Moment | null, endDate: moment.Moment | null }>
    ({ selectedDateFilter: "", startDate: null, endDate: null });
  const [filterFormErrors, setFilterFormErrors] = useState({ selectedDateFilter: { showError: false, message: "" }, });

  const { clinicName, patientName, dateOfAssessment } = useContext(AppointmentReport);

  let { id } = useParams() as any;

  useEffect(() => {
    getReports();
  }, [])

  useEffect(() => {
    if (reports.length !== filteredReports.length) {
      setShowCancelFilter(true);
    } else {
      setShowCancelFilter(false)
    }
  }, [filteredReports])

  useEffect(() => {
    if (id) {
      showSelectedReport(id);
    }
  }, [filteredReports])

  useEffect(() => {
    resetDefault();
  }, [selectedTab])

  function resetDefault() {
    let newFilterForm = { ...filterForm };
    newFilterForm.startDate = null;
    newFilterForm.endDate = null;
    setFilerForm((prevDetails) => ({ ...prevDetails, ...newFilterForm }));

    let newFilterFormErrors = { ...filterFormErrors };
    newFilterFormErrors.selectedDateFilter.showError = false;
    setFilterFormErrors((prevDetails) => ({ ...prevDetails, ...newFilterFormErrors }));

    setFilteredReports(reports);
  }

  function showSelectedReport(documentId: string) {
    let selectedReport = filteredReports.filter((report) => {
      return report.id === documentId;
    })

    if (selectedReport.length > 0) {
      setSelectedReport(selectedReport?.[0]);
      handleOpen("view");
    }
  }

  function filter(filterBy: ReportFilterTypes, query: string) {
    let filteredList = filteredReports.filter((report) => {
      return report[filterBy] === query
    })

    setFilteredReports(filteredList);
  }

  const handleOpen = useCallback((modal: "add" | "edit" | "view" | "bulkDelete" |
    "bulkDownload" | "generateLink" | "edit2") => {
    let newReportModals = reportModals;
    newReportModals[modal] = true;
    setReportModals((prevDetails) => ({ ...prevDetails, ...newReportModals }));
  }, [])

  const handleClose = useCallback((modal: "add" | "edit" | "view" | "bulkDelete" |
    "bulkDownload" | "generateLink" | "edit2", shouldUpdate = false) => {
    let newReportModals = reportModals;
    newReportModals[modal] = false;
    setReportModals((prevDetails) => ({ ...prevDetails, ...newReportModals }));

    searchParams.delete('fromAppointment');
    setSearchParams(searchParams);
    navigate("/dashboard/reports");

    if (shouldUpdate) {
      getReports();
    }
  }, [])

  useMemo(() => {
    if (clinicName !== '' && patientName !== '' && dateOfAssessment != null) {
      handleOpen("add");
    }
  }, [clinicName, patientName, dateOfAssessment])

  function getReports() {

    setLoading(true);

    apiService.getDoctorDocuments().then((result) => {

      let data = result.data;

      if (!data?.isSuccessful) {
        let newShowAlert = showAlert;
        newShowAlert.isError = true;
        newShowAlert.message = data?.errorMessage;
        newShowAlert.open = true;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        return;
      }

      setReports(data?.documents);
      setFilteredReports(data?.documents);
    }).catch(() => {
      let newShowAlert = showAlert;
      newShowAlert.isError = true;
      newShowAlert.message = "Unexpected Error";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
    }).finally(() => {
      setLoading(false);
    });
  }

  function filterReports(status: string) {
    return filteredReports.filter((report) => {
      return report.status === status
    })
  }

  function getPendingReports() {
    return filteredReports.filter((report) => {
      return report.status === "Pending" || report.status === "Assigned"
    })
  }

  const closeAlert = () => {
    let newShowAlert = showAlert;
    newShowAlert.open = false;
    setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
  }

  function search(searchedWord: string) {

    if (searchedWord.trim() === "") {
      resetDefault();
      return;
    }

    let searchArr = [] as IReport[];

    filteredReports.forEach((report) => {
      if (report.patientName.toLowerCase().includes(searchedWord.toLowerCase())) {
        searchArr.push(report);
      }

      if (report.clinic.toLowerCase().includes(searchedWord.toLowerCase())) {
        searchArr.push(report);
      }

      if (report.reportType.toLowerCase().includes(searchedWord.toLowerCase())) {
        searchArr.push(report);
      }

      if (report.priority.toLowerCase().includes(searchedWord.toLowerCase())) {
        searchArr.push(report);
      }
    })

    let distinctArr = searchArr.filter((value, index) => searchArr.indexOf(value) === index);

    setFilteredReports(distinctArr);
  }

  function draftReportsTicked(): boolean {
    const draftReports = filterReports("Draft");

    if (draftReports.length === 0) {
      return false;
    }

    return draftReports.every(report => tickedReports.includes(report.id));
  }

  function tickReport(reportId: string) {
    setTickedReports(prev => {
      if (prev.includes(reportId)) {
        return prev.filter(id => id !== reportId);
      } else {
        return [...prev, reportId];
      }
    });
  }

  function toggleAllDraftReports() {
    const draftReports = filterReports("Draft");

    if (draftReports.length === 0) {
      return false;
    }

    const allDraftIds = draftReports.map(report => report.id);

    setTickedReports(prev => {
      const allSelected = allDraftIds.every(id => prev.includes(id));
      return allSelected ? [] : [...allDraftIds];
    });
  }

  function isReportTicked(reportId: string): boolean {
    return tickedReports.includes(reportId);
  }

  async function bulkDownload() {
    setLoading(true);

    if (tickedReports.length === 0) {
      setLoading(false);
      return;
    }

    try {
      for (const reportId of tickedReports) {
        const selectedDocument = reports.find((report) => report.id === reportId);

        if (!selectedDocument) {
          continue;
        }

        const documentMedia: IFile[] = await fetchMediaArray(selectedDocument.mediaId);

        if (documentMedia.length === 0) {
          setShowAlert((prevDetails) => ({
            ...prevDetails, isError: true, message: `Files not Found for ${selectedDocument.patientName}`, open: true,
          }));
          continue;
        }

        const zip = new JSZip();
        let filesAddedCount = 0;

        for (const mediaFile of documentMedia) {
          try {
            const blob = await getMediaBlob(mediaFile.fileId);
            const innerFileName = mediaFile.fileName;

            if (!blob) {
              setShowAlert((prevDetails) => ({
                ...prevDetails, isError: true, message: `${mediaFile.fileName} not Found for ${selectedDocument.patientName}`, open: true,
              }));
              continue;
            }

            zip.file(innerFileName, blob);
            filesAddedCount++;
          } catch (error) {
            setShowAlert((prevDetails) => ({
              ...prevDetails, isError: true, message: `Error Getting Files for ${selectedDocument.patientName}`, open: true,
            }));
          }
        }

        if (filesAddedCount > 0) {
          try {
            const zipContent = await zip.generateAsync({ type: 'blob' });
            const fileName = getFileNameForZip(selectedDocument.patientName, selectedDocument.dateOfAssessment);
            saveAs(zipContent, fileName);
          } catch (zipError) {
            setShowAlert((prevDetails) => ({
              ...prevDetails, isError: true, message: `Error Downloading for ${selectedDocument.patientName}`, open: true,
            }));
          }
        }
      }
    } catch (err) {
      setShowAlert((prevDetails) => ({
        ...prevDetails, isError: true, message: "Error Downloading Files", open: true,
      }));
    } finally {
      handleClose("bulkDownload");
      setTickedReports([]);
      setLoading(false);
    }
  }

  async function bulkDelete() {
    setLoading(true);

    const data = { "documentIds": tickedReports }

    await apiService.bulkdDleteReports(data).then((result) => {

      let data = result.data;

      if (!data?.isSuccessful) {
        let newShowAlert = showAlert;
        newShowAlert.isError = true;
        newShowAlert.message = data?.errorMessage;
        newShowAlert.open = true;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        return;
      }

      let newShowAlert = showAlert;
      newShowAlert.isError = false;
      newShowAlert.message = `${tickedReports.length} Document Deleted`;
      newShowAlert.open = true;
      setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));

      setTickedReports([]);
      handleClose("bulkDelete", true);
    }).catch(() => {
      let newShowAlert = showAlert;
      newShowAlert.isError = true;
      newShowAlert.message = "Unexpected Error";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
    }).finally(() => {
      setLoading(false);
    });
  }

  function handleFilterForm() {

    setLoading(true);

    if (filterForm.selectedDateFilter.trim() === "") {
      let newFilterFormErrors = { ...filterFormErrors };
      newFilterFormErrors.selectedDateFilter.showError = true;
      setFilterFormErrors((prevDetails) => ({ ...prevDetails, ...newFilterFormErrors }));
      setLoading(false);
      return;
    }

    if (filterForm.startDate === null || filterForm.endDate === null) {
      let newShowAlert = { ...showAlert };
      newShowAlert.isError = true;
      newShowAlert.message = "Please Select a Date Range";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
      setLoading(false);
      return;
    }

    const start = moment(filterForm.startDate);
    const end = moment(filterForm.endDate);

    const newFilteredReports = [...filteredReports].filter((report) => {
      const reportDate = report[filterForm.selectedDateFilter as
        keyof Pick<IReport, 'createdDate' | 'dueDate' | 'dateOfAssessment' | 'submittedDate' | 'completedDate'>];

      if (!reportDate) {
        return false;
      }

      const date = moment(reportDate);
      return date.isSameOrAfter(start, 'day') && date.isSameOrBefore(end, 'day');
    });

    setFilteredReports(newFilteredReports);
    setLoading(false);
  }

  function getFilterDateValues(): ITextInput[] {
    const dateFilterValues: ITextInput[] = [
      { label: "Created Date", value: "createdDate" },
      { label: "Due Date", value: "dueDate" },
      { label: "Date of Assessment", value: "dateOfAssessment" },
      { label: "Submitted Date", value: "submittedDate" },
      { label: "Completed Date", value: "completedDate" }
    ];

    if (selectedTab === 0 || selectedTab === 1) {
      return dateFilterValues.slice(0, 3);
    }

    if (selectedTab === 2) {
      return dateFilterValues.slice(0, 4);
    }

    return dateFilterValues;
  }

  return (
    <Layout selectedIndex={2}>
      <div className="documentation-container">
        <MuiAlert error={showAlert.isError} message={showAlert.message} open={showAlert.open} onClose={closeAlert} />
        <div className="top-bar">
          <div className="row container-header animated animatedFadeInUp fadeInUp">
            <span className="location">Reports</span>
            <div className="box search-box">
              <Search onChange={search} />
            </div>
          </div>
          <div className="row-tab animated animatedFadeInUp fadeInUp">
            <Tabs titles={["Draft", "Pending", "Received", "Completed"]} selectedIndex={selectedTab}
              onClick={(value: number) => { setSelectedTab(value); }} />
            <Show hasAccess={true}>
              <NewButton text="+ Add Report" onClick={() => { handleOpen("add"); }} />
            </Show>
          </div>
          <div className="filter">
            <div className="input-wrapper">
              <div className="dropdown-wrapper">
                <InputDropDown type="search" title={"DATE*"} values={getFilterDateValues()} typeable={false}
                  showError={filterFormErrors.selectedDateFilter.showError}
                  onSelect={(value: any) => {
                    if (value?.length > 0) {
                      let newFilterForm = { ...filterForm };
                      newFilterForm.selectedDateFilter = value[0].value;
                      setFilerForm((prevDetails) => ({ ...prevDetails, ...newFilterForm }));
                    }
                  }} />
              </div>
              <DateRangePicker startPlaceHolder="FROM" endPlaceHolder="TO"
                onChange={(startDate, endDate) => {
                  let newFilterForm = { ...filterForm };
                  newFilterForm.startDate = moment(startDate).local();
                  newFilterForm.endDate = moment(endDate).local();
                  setFilerForm((prevDetails) => ({ ...prevDetails, ...newFilterForm }));
                }} />
            </div>
            <div className="button-wrapper">
              <DocumentButton text={'Filter'} onClick={handleFilterForm} loading={loading} />
            </div>
          </div>
        </div>

        {selectedTab === 0
          ? (<DraftReportsUI cancelFilter={showCancelFilter} resetDefault={resetDefault} reports={filterReports("Draft")}
            setSelectedReport={setSelectedReport} openView={handleOpen} filter={filter} areAllReportsTicked={draftReportsTicked}
            isReportTicked={isReportTicked} tickReport={tickReport} toggleAllReports={toggleAllDraftReports}
            showDelete={tickedReports.length > 0} showDownload={tickedReports.length > 0} />)
          : (<></>)
        }
        {selectedTab === 1
          ? (<PendingReportsUI cancelFilter={showCancelFilter} resetDefault={resetDefault} reports={getPendingReports()}
            setSelectedReport={setSelectedReport} openView={handleOpen} filter={filter} />)
          : (<></>)
        }
        {selectedTab === 2
          ? (<RecievedReportsUI cancelFilter={showCancelFilter} resetDefault={resetDefault} reports={filterReports("Submitted")}
            setSelectedReport={setSelectedReport} openView={handleOpen} filter={filter} />)
          : (<></>)
        }
        {selectedTab === 3
          ? (<CompletedReportsUI cancelFilter={showCancelFilter} resetDefault={resetDefault} reports={filterReports("Completed")}
            setSelectedReport={setSelectedReport} openView={handleOpen} filter={filter} />)
          : (<></>)
        }

        {reportModals.add
          ? (<AddReport open={reportModals.add} close={handleClose} />)
          : (<></>)
        }
        {reportModals.edit
          ? (<EditReport open={reportModals.edit} close={handleClose} selectedReportId={selectedReport.id} inScribing={selectedReport.inScribing} />)
          : (<></>)
        }
        {reportModals.edit2
          ? (<EditReport2 open={reportModals.edit2} close={handleClose} selectedReportId={selectedReport.id} inScribing={selectedReport.inScribing} />)
          : (<></>)
        }
        {reportModals.view
          ? (<ViewDocumentModal setShowAlert={setShowAlert} showAlert={showAlert} open={reportModals.view} close={handleClose}
            selectedDocument={selectedReport} openView={handleOpen} loading={loading} />)
          : (<></>)
        }
        {reportModals.bulkDownload
          ? (<ConfirmationPrompt open={reportModals.bulkDownload} onClose={() => { handleClose("bulkDownload", false); }}
            header={`Downloading ${tickedReports.length} Patient Record(s)`}
            onOk={bulkDownload} loading={loading}
            body={`You are about to download ${tickedReports.length} patient record(s) as ZIP files. This will include all associated 
              documents for the selected patients. Do you want to continue?`} />)
          : (<></>)
        }
        {reportModals.bulkDelete
          ? (<DeletePrompt open={reportModals.bulkDelete} close={() => { handleClose("bulkDelete", false); }}
            header={tickedReports.length > 1 ? `Deleting ${tickedReports.length} Documents` : `Deleting ${tickedReports.length} Document`}
            onDelete={bulkDelete} loading={loading} showAlert={showAlert} closeAlert={closeAlert}
            body={"Please note that this action is irreversible. This will remove all associated data and" +
              " appointment from calendar. Are you certain you wish to proceed with this operation?"} />)
          : (<></>)
        }
        {reportModals.generateLink
          ? (<GenerateLinkModal open={reportModals.generateLink} close={() => { handleClose("generateLink", false); }}
            selectedDocument={selectedReport} loading={loading} />)
          : (<></>)
        }
      </div>
    </Layout>
  );
}
